// ── Datencheck — Vollständigkeits-/Vertrauens-Aggregat ───────────────────────
//
// Macht sichtbar „habe ich alles erfasst & stimmt es?". Sammelt OFFENE PUNKTE
// (Dinge zum Klären) + eine QUELLEN-Liste, ausschließlich aus vorhandenen
// Client-Signalen — KEIN neues Backend, keine zweite Wahrheit:
//   • Offene Transfers      → buildTransferMap (transferMatcher.js)
//   • Fehlende Kaufpreis-Basis → buildFifoPool pool-Lots (fifo.js, costBasisUnknown)
//   • Ausgeblendetes Scam-Dust → scamDustTxIds (txView.js) — rein informativ
//   • Quellen + Import-Typ  → tx.exchange / tx.source + findExchange (exchanges.js)
//
// BEWUSST NICHT in v1: „Saldo weicht von der Börse ab". Das braucht einen ECHTEN
// Börsen-Saldo (reconcile.js erwartet `realBalances` als Nutzer-Eingabe) — es gibt
// keine automatische Live-Saldo-Quelle. Quellen zeigen daher ehrlich den
// Import-Typ (API/On-Chain/CSV), keinen Pass/Fail-Abgleich.
//
// Reine Daten-/ES-Module-Datei — testbar wie releaseAgenda.js.

import { buildTransferMap, isOpenTransfer } from './transferMatcher';
import { buildFifoPool, getTaxableEvents } from './fifo';
import { scamDustTxIds } from './txView';
import { findExchange } from './exchanges';
import { PORTFOLIO_FIAT_EXCLUDE } from './portfolioCalc';
import { COIN_MAP } from './coingecko';

// „Kaufpreis fehlt" gilt NUR für echte Erwerbe per Kauf/Swap. Transfer-Eingänge
// (`einzahlung` — z.B. Ledger-/On-Chain-Empfänge, Reward-Dust) und Income
// (Airdrop/Staking) sind KEINE Käufe und gehören nicht in „Kaufpreise ergänzen".
const NO_BASIS_TYPES = new Set(['kauf', 'swap']);

// Map-Schlüssel exakt wie buildTransferMap (tx._docId || String(tx.id)).
const txKey = (tx) => tx._docId || (tx.id != null ? String(tx.id) : undefined);

// Basis-Börse für Logo + Gruppierung: „Bitget Futures" → „Bitget". Findet die
// Quelle direkt; sonst Zusätze (Futures/Spot/…) wortweise abstreifen, bis eine
// bekannte Börse matcht. „Coinbase Wallet" bleibt erhalten (eigener Eintrag).
function baseExchangeName(name) {
  const direct = findExchange(name);
  if (direct && direct.id !== 'other') return direct.label;
  const words = String(name || '').trim().split(/\s+/);
  while (words.length > 1) {
    words.pop();
    const m = findExchange(words.join(' '));
    if (m && m.id !== 'other') return m.label;
  }
  return name;
}

/**
 * IDs der TX, die offene Lots OHNE Kaufpreis-Basis erzeugen (gleiche Logik wie
 * der no_basis-Punkt: nur echte Käufe/Swaps, Income ausgeschlossen). Für die
 * „Kaufpreise ergänzen"-Seite, um die betroffenen TX aufzulösen.
 * @param {Array} txs
 * @returns {Set<string>} sourceTransactionId-Set
 */
export function getNoBasisTxIds(txs) {
  const list = Array.isArray(txs) ? txs : [];
  const typeById = new Map();
  for (const tx of list) { const k = txKey(tx); if (k) typeById.set(k, tx.typ); }
  const pool = buildFifoPool(list).pool || {};
  const ids = new Set();
  for (const [coin, lots] of Object.entries(pool)) {
    if (PORTFOLIO_FIAT_EXCLUDE.has(coin)) continue;
    for (const lot of (lots || [])) {
      if (lot.costBasisUnknown && !lot.overConsumption) {
        if (!NO_BASIS_TYPES.has(typeById.get(lot.sourceTransactionId))) continue;
        if (lot.sourceTransactionId) ids.add(lot.sourceTransactionId);
      }
    }
  }
  return ids;
}

/**
 * Die TX, die OFFENE Transfers sind (Eingang/Ausgang ohne bestätigten Gegenpart)
 * — gleiche Quelle (isOpenTransfer) wie Wallet-Zählung. Für die „Zu klären"-Seite.
 * @param {Array} txs
 * @param {Object} [marketData]
 * @returns {Array} TX-Objekte
 */
export function getOpenTransferTxs(txs, marketData = {}) {
  const list = Array.isArray(txs) ? txs : [];
  const tmap = buildTransferMap(list);
  let dustIds;
  try { dustIds = scamDustTxIds(list, marketData); } catch { dustIds = new Set(); }
  const isDust = (tx) => dustIds.has(txKey(tx));
  return list.filter((tx) => isOpenTransfer(tx, tmap, isDust));
}

// „Extern empfangen" — ein Krypto-Eingang OHNE Gegenpart im eigenen Portfolio
// (kein gematchter/offener Transfer, keine Custodial-Lieferung). Das sind Coins,
// die von AUSSEN reinkamen: Geschenk von einer Person, Empfang von einer noch
// nicht importierten eigenen Wallet, oder anderswo gekauft. Der Nutzer sieht zwar
// „empfangen", sollte aber die QUELLE bestätigen (relevant für Anschaffung).
//
// Abgrenzung gegen Rauschen — bewusst NUR Eingänge mit echtem Marktwert:
//   • typ === 'einzahlung' (nicht Staking/Airdrop = eigene Income-Kategorie)
//   • Coin ∈ COIN_MAP → echtes, bewertbares Asset. Schließt unbekannte/Scam-Token
//     (NAKMAK, NOODLE …) aus, die ohnehin keinen Preis haben.
//   • kein Dust, kein Fiat, kein bereits gematchter/offener Transfer.
function isExternalReceive(tx, tmap, isDust, marketData = {}) {
  if (tx.typ !== 'einzahlung') return false;
  const coin = String(tx.coin || tx.sym || '').toUpperCase();
  if (!coin || PORTFOLIO_FIAT_EXCLUDE.has(coin)) return false;
  if (!(Number(tx.menge) > 0)) return false;
  if (!COIN_MAP[coin]) return false;                       // unbekannter/Scam-Token → nicht hier
  // NUR WERTHALTIGE Eingänge: 0-€-Token (Scam/Memecoin-Airdrops wie NOODLE/NAKMAK —
  // inzwischen via Resolver/Jupiter in COIN_MAP, daher reicht die COIN_MAP-Prüfung nicht
  // mehr) sind KEINE „Quelle zuordnen"-Fälle. Bedingung: Marktpreis ODER erfasster
  // Gegenwert (countervalue, z.B. aus der Ledger-CSV) > 0. Synthetische SPL-IDs (spl:…)
  // ohne echten Kurs fallen damit raus.
  const id = COIN_MAP[coin]?.id;
  const px = id ? Number(marketData?.[id]?.price) : 0;
  const cv = Number(tx.countervalue) || 0;
  if (!(px > 0) && !(cv > 0)) return false;
  if (typeof isDust === 'function' && isDust(tx)) return false;
  if (tx.externalTransfer || tx.ownWalletTransfer) return false;
  const st = tmap?.get(txKey(tx))?.matchStatus;
  if (st === 'matched' || st === 'unmatched') return false; // hat Gegenpart / schon offen geführt
  return true;
}

/**
 * Die TX, die externe Empfänge ohne bestätigte Quelle sind. Für die „Zu klären"-
 * Seite (Nutzer ordnet Quelle zu: Geschenk / eigener Transfer / woanders gekauft).
 * @param {Array} txs
 * @param {Object} [marketData]
 * @returns {Array} TX-Objekte
 */
export function getExternalReceiveTxs(txs, marketData = {}) {
  const list = Array.isArray(txs) ? txs : [];
  const tmap = buildTransferMap(list);
  let dustIds;
  try { dustIds = scamDustTxIds(list, marketData); } catch { dustIds = new Set(); }
  const isDust = (tx) => dustIds.has(txKey(tx));
  return list.filter((tx) => isExternalReceive(tx, tmap, isDust, marketData));
}

/**
 * @param {Object} args
 * @param {Array}  args.txs
 * @param {Object} [args.marketData]  { [coinId]: { price } } — für das Scam-Dust-Wert-Veto.
 * @returns {{
 *   openCount:number,
 *   points:Array<{kind:string, count:number, items:Array}>,
 *   dustCount:number,
 *   sources:Array<{name:string, count:number, kind:('api'|'chain'|'csv'|'manual')}>,
 *   connectedCount:number, totalSources:number
 * }}
 *   points: 'open_transfer' (Eingänge/Ausgänge ohne Gegenpart) | 'no_basis' (Lots
 *   ohne Kaufpreis). openCount = Σ Einzelposten (echte „Dinge zu klären"), Dust
 *   zählt NICHT mit (bewusst ausgeblendet, nur informativ).
 */
export function buildDataCheck({ txs, marketData = {} } = {}) {
  const list = Array.isArray(txs) ? txs : [];

  // Scam-Dust einmal bestimmen — für Offene-Transfer-Ausschluss UND Zähler.
  let dustIds;
  try { dustIds = scamDustTxIds(list, marketData); } catch { dustIds = new Set(); }
  const isDust = (tx) => dustIds.has(txKey(tx));

  // 1) Offene Transfers — Ein-/Ausgänge ohne bestätigten Gegenpart. EINE Quelle
  // (isOpenTransfer aus transferMatcher) — IDENTISCH zur Wallet-„offene Transfers".
  const tmap = buildTransferMap(list);
  const openTransfers = [];
  const externalReceives = [];
  for (const tx of list) {
    if (isOpenTransfer(tx, tmap, isDust)) {
      openTransfers.push({
        coin: tx.coin, menge: Number(tx.menge) || 0, datum: tx.datum,
        exchange: tx.exchange || '', typ: tx.typ,
      });
    } else if (isExternalReceive(tx, tmap, isDust, marketData)) {
      // Eingang von außen ohne Gegenpart (Geschenk / fremde/eigene Wallet) — Quelle
      // bestätigen. Getrennt von open_transfer (das hat einen plausiblen Gegenpart).
      externalReceives.push({
        coin: tx.coin, menge: Number(tx.menge) || 0, datum: tx.datum,
        exchange: tx.exchange || '', typ: tx.typ,
      });
    }
  }

  // 2) Offene Lots ohne Kaufpreis-Basis (costBasisUnknown) — verzerren Werte.
  // NUR echte Käufe/Swaps (NO_BASIS_TYPES) zählen als „Kaufpreis fehlt". Bewusst
  // ausgeschlossen: Transfer-Eingänge (`einzahlung` — Ledger-/On-Chain-Empfänge,
  // Reward-Dust; das ist eine Herkunfts-/Matching-Frage, kein Kauf) und Income
  // (systematisch über Marktkurs am Zufluss-Tag bewertet, useHistoricalPrices).
  const typeById = new Map();
  for (const tx of list) { const k = txKey(tx); if (k) typeById.set(k, tx.typ); }
  const _fifo = buildFifoPool(list);
  const pool = _fifo.pool || {};
  const fifoResults = _fifo.results || [];
  const noBasis = [];
  for (const [coin, lots] of Object.entries(pool)) {
    if (PORTFOLIO_FIAT_EXCLUDE.has(coin)) continue;
    for (const lot of (lots || [])) {
      if (lot.rest > 1e-9 && lot.costBasisUnknown && !lot.overConsumption) {
        if (!NO_BASIS_TYPES.has(typeById.get(lot.sourceTransactionId))) continue;
        noBasis.push({ coin, menge: lot.rest, datum: lot.datum });
      }
    }
  }
  // Disposed lots with unknown basis are no longer in the pool (rest=0) —
  // scan taxable events to catch them too (e.g. swap-acquired coin later sold).
  const noBasisCoins = new Set(noBasis.map(n => n.coin));
  for (const evt of getTaxableEvents(fifoResults)) {
    if (!evt.costBasisUnknown || evt.incomplete) continue;
    const coin = evt.typ === 'swap' ? evt.coinOut : evt.coin;
    if (!coin || PORTFOLIO_FIAT_EXCLUDE.has(coin)) continue;
    if (noBasisCoins.has(coin)) continue;
    noBasisCoins.add(coin);
    noBasis.push({ coin, menge: 0, datum: evt.datum });
  }

  // 3) Scam-Dust (Address-Poisoning) — ausgeblendet, rein informativ.
  const dustCount = dustIds.size;

  // 4) Quellen — je tx.exchange, Import-Typ aus tx.source abgeleitet.
  const srcMap = new Map();
  for (const tx of list) {
    const name = tx.exchange;
    if (!name) continue;
    let s = srcMap.get(name);
    if (!s) { s = { name, srcTypes: new Set(), count: 0 }; srcMap.set(name, s); }
    s.srcTypes.add(tx.source || 'manual');
    s.count += 1;
  }
  const sources = [...srcMap.values()].map((s) => {
    const meta = findExchange(s.name);
    const kind = s.srcTypes.has('exchange_api') ? 'api'
      : s.srcTypes.has('chain_import') ? 'chain'
      : s.srcTypes.has('csv_import') ? 'csv'
      : 'manual';
    return { name: s.name, count: s.count, kind, metaKind: meta?.kind || 'other', logoName: baseExchangeName(s.name) };
  }).sort((a, b) => b.count - a.count);

  const connectedCount = sources.filter((s) => s.kind === 'api' || s.kind === 'chain').length;

  const points = [];
  if (openTransfers.length) points.push({ kind: 'open_transfer', count: openTransfers.length, items: openTransfers });
  if (externalReceives.length) points.push({ kind: 'external_receive', count: externalReceives.length, items: externalReceives });
  if (noBasis.length) points.push({ kind: 'no_basis', count: noBasis.length, items: noBasis });

  const openCount = openTransfers.length + externalReceives.length + noBasis.length;

  return {
    openCount,
    points,
    dustCount,
    sources,
    connectedCount,
    totalSources: sources.length,
  };
}
